import { createContext, useContext, useMemo, useReducer } from 'react';
import invariant from 'react-utils/invariant';
import { DefaultBulkActionsModalsMap } from '../components/BulkActions';
import { SELECTION_METHOD_BY_IDS } from '../constants/selectionMethods';
import { useBulkActionsDataFetcher } from '../hooks/useBulkActionsDataFetcher';
import { createBulkActionsSelectionStateObject, DefaultBulkActionsReducer } from '../reducers/defaultBulkActionsReducer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BulkActionsStateContext = /*#__PURE__*/createContext(undefined);
const BulkActionsDispatchContext = /*#__PURE__*/createContext(undefined);

/**
 * DO NOT USE THIS HOOK DIRECTLY, INTERNAL USE ONLY
 * instead you should use customer-data-bulk-actions/hooks/useBulkActionsState
 */
export const useBulkActionsStateContext = () => {
  const context = useContext(BulkActionsStateContext);
  if (context === undefined) {
    throw new Error('useBulkActionsStateContext must be called under a BulkActionsContextProvider');
  }
  return context;
};

/**
 * DO NOT USE THIS HOOK DIRECTLY, INTERNAL USE ONLY
 * instead you should use customer-data-bulk-actions/hooks/useBulkActionsDispatch
 */
export const useBulkActionsDispatchContext = () => {
  const context = useContext(BulkActionsDispatchContext);
  if (context === undefined) {
    throw new Error('useBulkActionsDispatchContext must be called under a BulkActionsContextProvider');
  }
  return context;
};
const DefaultBulkActionsContextProviderDefaultState = {
  currentModal: undefined,
  selection: createBulkActionsSelectionStateObject({
    selectionMethod: SELECTION_METHOD_BY_IDS,
    selectionIds: [],
    selectionCrmSearchQuery: undefined,
    selectionObjectTypeId: '' //this will be overwritten in BulkActionsContextProvider by BulkActionsContextProvider's props.objectTypeId
  }),
  cache: {
    byObjectTypeId: {},
    byCrmSearchQueryObject: {}
  },
  warmupCacheFor: undefined,
  error: {},
  loading: {}
};

/**
 * A helper component so we can call useBulkActionsDataFetcher within BulkActionsContextProvider
 * We cannot call it within BulkActionsContextProvider directly because it would be above the contexts
 */
const DataFetcher = () => {
  useBulkActionsDataFetcher();
  return null;
};
export const BulkActionsContextProvider = ({
  children,
  reducer = DefaultBulkActionsReducer,
  modalsByKey = DefaultBulkActionsModalsMap,
  defaultState = DefaultBulkActionsContextProviderDefaultState,
  objectTypeId
}) => {
  invariant(objectTypeId, "BulkActionsContextProvider.props.objectTypeId - expected 'string' but received: %s");
  defaultState.selection.selectionObjectTypeId = objectTypeId;
  const [state, dispatch] = useReducer(reducer, defaultState);
  const openModal = useMemo(() => {
    if (!state.currentModal) {
      return null;
    }
    const Modal = modalsByKey[state.currentModal.modalKey];
    const propsForModal = state.currentModal.props || {};
    return /*#__PURE__*/_jsx(Modal, Object.assign({}, propsForModal));
  }, [modalsByKey, state.currentModal]);
  return /*#__PURE__*/_jsx(BulkActionsDispatchContext.Provider, {
    value: dispatch,
    children: /*#__PURE__*/_jsxs(BulkActionsStateContext.Provider, {
      value: state,
      children: [openModal || /*#__PURE__*/_jsx("span", {
        "data-test-id": "bulk-actions-no-modal-open"
      }), children, /*#__PURE__*/_jsx(DataFetcher, {})]
    })
  });
};